<template>

  <!-- XOL-67 (V2, v2.1; decisions 153, 158 Q2/Q3; v2.2: decisions 168 to 174): the Create RTA form is the REQUEST form. It records where the creator wants options found (the
       requested point, the locality, the requirements), not Option 1. Top to bottom (v2.2 UX spec C1): the Client (a Tower Co picks itself or a Telco; a Telco has
       none), the Site Name and the Site ID (both required of every creator, Telco included; the name is pre-filled from the map point and can be typed over),
       then Station Type and Locality: the LEFT column. v2.2 decision 176: the structure fields are the RIGHT column, and the map comes last (RtaRequestFields draws
       both columns and the map; the left column's first three fields are passed to it in its `lead` slot). Editing an existing request is Step 1 (V3), not this form. -->
  <div class="rta-create">

    <!-- Said to a screen reader each time the pre-fill changes the name (create only; typing is the user's own). -->
    <span class="rta-sr-only" aria-live="polite">{{ suggestedNote }}</span>

    <!-- v2.2 decision 176: the fields above the map are two columns, drawn by RtaRequestFields. The LEFT column starts with these, in its `lead` slot (so they share its
         one el-form): the Client, the Site Name with its hint directly under it, and the Site ID; then it adds Station Type and Locality. The right column is the
         structure fields. The fields are keyed (a v-if/v-else pair would reuse one el-form-item). -->
    <rta-request-fields
          :key="'fields-' + formKey"
          v-model="values"
          :errors="errors"
          :stacked="!!isMediumOrLess"
          @change="onChanged">

      <template #lead>

        <!-- XOL-67 (B3), decision 1: a Telco has no picker (its client is its own organisation, set when the form is sent); a Tower Co picks itself or a
             Telco. Keyed so a reset starts the picker empty (it keeps its own copy of the value). -->
        <select-organisation
              v-if="showClientPicker"
              ref="client"
              data-field="client"
              :key="'client-' + formKey"
              v-model="clientValue"
              label="Client"
              placeholder="Select client"
              :type="['Telco']"
              include-own
              required
              filterable
              aria-label="Client"
              :error="errors.client"
              :error-id="ids.client"
               />

        <text-input
              ref="siteName"
              key="name-input"
              class="rta-create__name"
              data-field="siteName"
              label="Site Name"
              aria-label="Site Name"
              required
              :maxlength="120"
              :value="values.siteName"
              placeholder="Site Name"
              :error="errors.siteName || null"
              :error-id="errors.siteName ? ids.siteName : null"
              :described-by="nameHint ? ids.nameHint : null"
              :validate-event="false"
              @focus="onNameFocus"
              @blur="onNameBlur"
              @change="onNameChange" />
        <small v-if="nameHint" key="name-hint" :id="ids.nameHint" class="text-muted rta-note rta-create__name-hint" :class="{ 'has-error': !!errors.siteName }" data-note="name-hint">{{ nameHint }}</small>

        <text-input
              ref="siteId"
              key="id-input"
              class="rta-create__id"
              data-field="siteId"
              label="Site ID"
              aria-label="Site ID"
              required
              :maxlength="16"
              :value="values.siteId"
              placeholder="Site ID"
              :error="errors.siteId || null"
              :error-id="errors.siteId ? ids.siteId : null"
              :validate-event="false"
              @change="onIdChange" />

      </template>

    </rta-request-fields>

  </div>

</template>

<script>

import { ENABLE_SAVE_EVENT } from '@/components/Data'

import { generateRequestId } from '@/lib/axiosClient'

import RtaCommandMixins from '@/mixins/RtaCommandMixins'

import * as rtaStage from '@/lib/rtaStage'
import * as rtaOption from '@/lib/rtaOption'
import { coordinate, createNominal, createNameHint, generatedNameFrom, nominalFieldErrors, nominalFieldProblem } from '@/lib/rtaNominal'
import { lookupPlaceResults, NAME_DEBOUNCE_MS, GOOGLE_WAIT_MS } from '@/lib/rtaNameLookup'
import { emptyRequestValues, localityPath, requestProblems, CREATE_FIELDS, FIELD_ORDER } from '@/lib/rtaRequestForm'

import SelectOrganisation from '@/components/atoms/selects/SelectOrganisation'

import RtaRequestFields from '@/components/requests/acquire/RtaRequestFields'

// The copy of the inline errors (UX spec 6).
var MSG_CLIENT = 'Select the client this RTA is for.'

export default {
  mixins: [RtaCommandMixins],

  components: {
    RtaRequestFields,
    SelectOrganisation
  },

  props: {

    // A client to start with (the Tower Co's picker); a Telco's client is always its own organisation.
    clientId: {
      type: Number,
      default: null
    },

    tableRef: {
      type: String,
      default: 'rta-action-table'
    }

  },



  computed: {

    // The store, as computed (not data()): it hydrates after the first paint.
    viewerOrg () {
      return this.org()
    },

    viewerProfile () {
      return this.profile()
    },

    // Decision 1: a Telco creator is the client, with no picker. A Tower Co creator picks itself or a Telco.
    showClientPicker () {
      return this.isTowerCo()
    },

    // Is there a requested point (both coordinates, P-S22)? Google is asked only then.
    hasPoint () {
      return coordinate(this.values.latitude) !== null && coordinate(this.values.longitude) !== null
    },

    pointKey () {
      return this.hasPoint ? coordinate(this.values.latitude) + ',' + coordinate(this.values.longitude) : ''
    },

    // Google's answer for THIS point (an answer for an earlier point is not this one's).
    lookedUp () {
      return !!this.lookup && this.lookup.key === this.pointKey
    },

    // The generated BASE name and where it came from, for the point as it stands: Google's answer for it when it has come, else the locality. This is
    // what the Site Name line shows and what Save sends (a name the server may number); it never waits for Google.
    nameInfo () {
      return generatedNameFrom(
        this.lookedUp ? this.lookup.geocode : null,
        this.lookedUp ? this.lookup.places : null,
        localityPath(this.values)
      )
    },

    // Is the point placed and Google not yet answered for it? The Site Name keeps its previous suggestion meanwhile (UX spec C2), and the hint says it is being looked up.
    lookingUp () {
      return this.hasPoint && !this.lookedUp
    },

    // The hint under the Site Name (UX spec C2): where the pre-filled name came from; none once the user has typed.
    nameHint () {
      return createNameHint({
        name: this.values.siteName,
        source: this.prefillSource,
        hasPoint: this.hasPoint,
        looking: this.lookingUp,
        typed: this.nameTyped
      })
    },

    // The ids of the error messages and the hint, for aria-describedby.
    ids () {
      var base = 'rta-create-' + this._uid

      return { client: base + '-client-error', siteName: base + '-name-error', siteId: base + '-id-error', nameHint: base + '-name-hint' }
    }

  },



  methods: {

    /**
     XOL-675: the one place the form's starting values are built. `resetData()` re-applies it, so a second Acquire in the same page load starts from the same
     state as the first. The client starts empty (a Tower Co picks it; a Telco's is its own organisation, put in the body when the form is sent).
     */
    initialState () {
      return {
        values: emptyRequestValues(),
        clientValue: this.clientId ? this.clientId : null,
        errors: {},
        // Google's raw answer for a point: { key, geocode, places } (see lookupPlaceResults), or null.
        lookup: null,
        // The Site Name (v2.2, decision 169): suggested from the map point (else the locality) until the user types, and always theirs once they have. `nameTyped`:
        // a `change` of the field, which includes clearing it. `nameFocused`: the field has the focus, and a suggestion never overwrites it. `prefillSource`: where
        // the name last copied into the field came from (the `siteNameSource` that goes with it); `suggestedNote`: what a screen reader hears of it.
        nameTyped: false,
        nameFocused: false,
        prefillSource: '',
        suggestedNote: '',
        saving: false
      }
    },



    // Only on success and on close. A failed save keeps the form for a retry.
    resetData () {
      this.cancelLookup()
      Object.assign(this, this.initialState())
      this.formKey += 1
    },



    /* ---------------------------------------------------------------------
       The Site Name and the Site ID
    --------------------------------------------------------------------- */

    // The suggestion for the point as it stands goes into the field: only while the user has not typed in it, it does not have the focus, and Google has
    // answered for the point (until then the previous suggestion stays). Never overwrites what the user is typing or has typed.
    applyPrefill () {
      var info = this.nameInfo

      // While Google is being asked about the point, the field keeps the previous suggestion; with none yet (a locality and a point at once) the locality's
      // name goes in rather than a blank, and Google's replaces it when it answers.
      if (this.nameTyped || this.nameFocused || (this.lookingUp && (this.values.siteName || '') !== '')) {
        return
      }

      this.prefillSource = info.source

      if ((this.values.siteName || '') === info.name) {
        return
      }

      this.values = Object.assign({}, this.values, { siteName: info.name || null })

      if (info.name) {
        this.suggestedNote = 'Site Name suggested: ' + info.name + '.'

        if (this.errors.siteName) {
          this.setError('siteName', '')
        }
      } else {
        this.suggestedNote = ''
      }
    },

    onNameFocus () {
      this.nameFocused = true
    },

    // Leaving the field: a suggestion that came while it had the focus (and was not typed over) goes in now.
    onNameBlur () {
      this.nameFocused = false
      this.applyPrefill()
    },

    // The user changed the Site Name (a `change`: on leaving the field or Enter, clearing it included): it is theirs from here on, and checked now.
    onNameChange (value) {
      this.nameTyped = true
      this.suggestedNote = ''
      this.values = Object.assign({}, this.values, { siteName: value })
      this.setError('siteName', nominalFieldProblem('siteName', value))
    },

    onIdChange (value) {
      this.values = Object.assign({}, this.values, { siteId: value })
      this.setError('siteId', nominalFieldProblem('siteId', value))
    },

    // One field's message: shown, or gone when it is empty.
    setError (field, message) {
      var next = Object.assign({}, this.errors)

      if (message) {
        next[field] = message
      } else {
        delete next[field]
      }

      this.errors = next
    },



    // RtaRequestFields changed `changes` (wire keys): a message about a field goes when the field is changed. The point's two coordinates answer to one message.
    onChanged (changes) {
      var next = Object.assign({}, this.errors)
      var keys = Object.keys(changes)

      if (keys.indexOf('latitude') !== -1 || keys.indexOf('longitude') !== -1) {
        delete next.latitude
        delete next.longitude
      }

      keys.forEach(function (k) {
        delete next[k]

        if (['region', 'district', 'subdistrict', 'zone'].indexOf(k) !== -1) {
          delete next.state
        }
      })

      this.errors = next
    },



    /* ---------------------------------------------------------------------
       The generated Site Name (decision 158 Q3): ask Google what is at the point, once it rests
    --------------------------------------------------------------------- */

    // The loaded `window.google` (gmap-vue loads it with the `places` library, see GoogleMap), or null. Save never waits for it.
    googleApi () {
      var ask = this.$gmapApiPromiseLazy

      if (typeof ask !== 'function') {
        return Promise.resolve(window.google || null)
      }

      // A script that never loads (blocked, no network) must not leave "Looking up…" on the line for ever: after GOOGLE_WAIT_MS it is "no Google".
      return new Promise(function (resolve) {
        var timer = setTimeout(function () { resolve(null) }, GOOGLE_WAIT_MS)

        ask().then(function (google) {
          clearTimeout(timer)
          resolve(google || window.google || null)
        }, function () {
          clearTimeout(timer)
          resolve(null)
        })
      })
    },

    cancelLookup () {
      if (this._nameTimer) {
        clearTimeout(this._nameTimer)
        this._nameTimer = null
      }

      // An answer on its way for an earlier point is stale from here on.
      this._nameSeq = (this._nameSeq || 0) + 1
    },

    // The point changed: forget the old answer, and (with a point) ask again after a pause. No point: no call.
    scheduleLookup () {
      var self = this
      var key = this.pointKey

      this.cancelLookup()
      this.lookup = null

      if (!this.hasPoint) {
        return
      }

      this._nameTimer = setTimeout(function () {
        self._nameTimer = null
        self.runLookup(key)
      }, NAME_DEBOUNCE_MS)
    },

    runLookup (key) {
      var self = this
      var seq = this._nameSeq
      var point = { lat: coordinate(this.values.latitude), lng: coordinate(this.values.longitude) }

      return this.googleApi().then(function (google) {
        return lookupPlaceResults(google, point)
      }).then(function (raw) {
        // The point moved, or the form closed or was reset (both bump the sequence), while Google was answering: this answer is for another place.
        if (seq !== self._nameSeq || key !== self.pointKey) {
          return
        }

        self.lookup = { key: key, geocode: raw.geocode, places: raw.places }
      })
    },



    /**
     Synchronous check for the Popup's :before-save gate: sets the field errors and returns false, and puts the focus on the first invalid field. Element's
     validate() is not relied on (plan 3.1 step 2). The sentences are the server's 422 ones (rtaNominal), so the form and the API say the same thing.
     */
    validateForm () {
      var errors = requestProblems(this.values, CREATE_FIELDS)

      if (this.showClientPicker && !this.clientValue) {
        errors.client = MSG_CLIENT
      }

      this.errors = errors

      var valid = Object.keys(errors).length === 0

      if (!valid) {
        this.focusFirstInvalid(errors)
      }

      return valid
    },



    // Accessibility: the first invalid field (in form order) takes focus, once the error is drawn.
    focusFirstInvalid (errors) {
      for (var i = 0; i < FIELD_ORDER.length; i++) {
        if (errors[FIELD_ORDER[i]]) {
          this.$nextTick(this.focusField.bind(this, FIELD_ORDER[i]))
          return FIELD_ORDER[i]
        }
      }

      return null
    },



    focusField (field) {
      var root = this.$el
      var node = root && typeof root.querySelector === 'function' ? root.querySelector('[data-field="' + field + '"]') : null
      var input = node ? node.querySelector('input') : null

      if (input && typeof input.focus === 'function') {
        input.focus()
      }
    },



    onSave () {

      if (this.saving) {
        return
      }

      if (!this.validateForm()) {
        this.$emit('save-failed')
        return
      }

      this.saving = true
      this.submit()
    },



    /**
     The client the RTA is for: a Telco's is always its own organisation (the server forces it too); a Tower Co's is the one it picked.
     */
    clientIdAtSave () {
      return this.showClientPicker ? this.clientValue : this.org().id
    },



    /**
     The body of POST /actions/rta (API plan DEV-85): the client, ONE idempotency key per click, and the request as `nominal`. `idempotencyKey` is made
     here, so a retry the transport makes after a 5xx carries the same key (a replay is answered 200 `duplicate: true`), a new click a new one (DEV-26).
     The Site Name and the Site ID go for every creator (decision 168). The name is what the field holds, never numbered (the server numbers a repeated
     suggestion): its source is the suggestion's, or 'manual' once the user has typed (decision 169).
     */
    buildBody () {
      var values = Object.assign({}, this.values, { siteNameSource: this.nameTyped ? 'manual' : this.prefillSource })

      return {
        client_id: this.clientIdAtSave(),
        idempotencyKey: generateRequestId(),
        nominal: createNominal(values)
      }
    },



    /**
     The one call: POST /actions/rta, quiet (rtaCommand toasts nothing: every outcome comes back here and the SPA words it). The Popup is told with `saved`
     or `save-failed`.
     */
    submit () {
      var self = this
      var ctx = this.createContext()

      return this.rtaCommand('post', '/actions/rta', this.buildBody(), 'RTA').then(function (result) {
        if (self._isDestroyed) {
          return result
        }

        if (result.ok) {
          try {
            self.finished(result.data, ctx)
          } catch (e) {
            self.finishFailed()
          }
        } else {
          self.failed(result)
        }

        return result
      })
    },



    /**
     Something after the server's 200 threw (a toast, a refresh, a listener): the RTA may well exist, but the Popup is waiting for an answer and Save must never
     stay dead. The form is released as a failure and the neutral "not with what was expected" line says to check the list.
     */
    finishFailed () {
      this.saving = true
      this.failed({ ok: false, unanswered: true, silent: false, err: null, failure: { status: null, code: null, data: null, errors: null } })
    },



    // Who the RTA is for and who hears of it, taken at the click (the form is reset when the answer comes).
    createContext () {
      var tower = this.showClientPicker
      var own = this.org()
      var id = this.clientIdAtSave()

      return {
        tower: tower,
        // 'self': a Tower Co creating for its own organisation (nobody to e-mail); 'client': a Tower Co for a Telco; 'towerco': a Telco's own request.
        target: tower ? (own && own.id === id ? 'self' : 'client') : 'towerco',
        other: tower ? this.selectedClientName() : this.towerCoName()
      }
    },



    // The name of the client picked, for the toast ("… at Telco Beta are being e-mailed").
    selectedClientName () {
      var id = this.clientIdAtSave()
      var own = this.org()

      if (own && own.id === id) {
        return own.name || ''
      }

      var groups = this.getConfig().orgGroups
      var name = ''

      if (Array.isArray(groups)) {
        groups.forEach(function (group) {
          ;(group.options || []).forEach(function (item) {
            if (item.value === id) {
              name = item.label || ''
            }
          })
        })
      }

      return name
    },



    // The server has answered for the whole create: say so, refresh the list, tell the Popup, reset.
    finished (data, ctx) {
      var response = data || {}

      this.toastCreated(response, ctx)

      this.$events.emit(this.tableRef + '-refresh-table')

      // send() skips this in quiet mode.
      this.refreshStats()

      this.$emit('update-stats')
      this.$emit('saved')

      // After the emits, so one that throws leaves `saving` set and falls through to failed(). The reset clears `saving`.
      this.resetData()
    },



    /**
     "RTA created: {name} ({reference})." with the STORED name from the response (the server numbered it, decision 160), then what the e-mail did (the
     counts-only notice of the response): "2 recipients at TowerCo Alpha are being e-mailed." A Send E-mails off RTA says so in the sentence. A Tower Co
     client (nobody to e-mail), a replay (`duplicate: true`, DEV-26) and an answer with no notice say the sentence alone.
     */
    toastCreated (response, ctx) {
      var action = response.action && typeof response.action === 'object' ? response.action : {}
      var nominal = action.state && action.state.nominal && typeof action.state.nominal === 'object' ? action.state.nominal : {}
      var notice = response.notice && typeof response.notice === 'object' ? response.notice : null
      var off = !!notice && notice.status === 'not_sent' && notice.reason === 'mail_off'

      var sentence = rtaOption.sentence('create', {}, {
        name: nominal.siteName || action.name || '',
        reference: action.reference || rtaStage.rtaReference(action.id),
        target: ctx.target,
        mailOff: off || !rtaStage.mailOn(action)
      })

      this.rtaToast(sentence, response, { orgs: ctx.other ? [ctx.other] : [], actor: ctx.tower ? 'towerco' : 'client', towerCo: this.towerCoName() })
    },



    /**
     Any refusal, a 422 included: the form stays as typed. Idempotent. The server's field errors (`nominal.<field>`, `client_id`, API DEV-85) go on the
     fields, in plain copy, and the first takes focus; anything the form has no field for, and every other refusal, is worded by rtaRefusal.
     */
    failed (result) {
      if (!this.saving) {
        return
      }

      this.saving = false

      // The Popup answers this by putting focus back on its Save button in the next tick, so the errors (and the focus on the first invalid field,
      // queued by them) come after it.
      this.$emit('save-failed')

      var failure = result && result.failure ? result.failure : {}

      if (failure.status === 422 && this.applyServerErrors(failure.data, result)) {
        return
      }

      this.rtaRefuse(result, { command: 'create', towerCo: this.towerCoName() })
    },



    /**
     Put the server's 422 on the form. Returns true when it mapped at least one field (the Site Name and the Site ID have inputs now: `site_name_taken` and
     `site_id_taken` go on them). Keys that have no field here (an `option` body of a stale page, a `client_id` a Telco cannot fix) are said in a toast as well.
     */
    applyServerErrors (data, result) {
      var mapped = nominalFieldErrors(data)
      var next = {}
      var left = []
      var self = this

      Object.keys(mapped.fields).forEach(function (f) {
        next[f] = mapped.fields[f]
      })

      mapped.rest.forEach(function (key) {
        if (key === 'client_id' && self.showClientPicker) {
          next.client = MSG_CLIENT
        } else {
          left.push(key)
        }
      })

      if (Object.keys(next).length === 0) {
        return false
      }

      // (validateForm ran first and passed, so there is no earlier message to keep)
      this.errors = next
      this.focusFirstInvalid(this.errors)

      if (left.length > 0) {
        this.rtaRefuse(result, { command: 'create', towerCo: this.towerCoName() })
      }

      return true
    },



    onClose () {
      this.$emit('update-stats')
      this.resetData()
    },



    onCloseDialog () {
      this.resetData()
    }


  },



  watch: {
    pointKey: function () { this.scheduleLookup() },
    // The suggestion follows the point, the locality and Google's answer (applyPrefill decides whether it may go in).
    nameInfo: function () { this.applyPrefill() },
    lookingUp: function () { this.applyPrefill() },
    clientValue: function () {
      if (this.errors.client) {
        var next = Object.assign({}, this.errors)

        delete next.client
        this.errors = next
      }
    }
  },




  data () {

    return Object.assign({
      // Bumped by a reset: remounts the client picker and the fields, which keep their own copy of a value.
      formKey: 0
    }, this.initialState())

  },  // data



  created () {

    this.$events.emit(ENABLE_SAVE_EVENT)
  },


  mounted () {
    // A form opened with a locality or a point already in it (a reset starts empty, so this is for a starting value).
    this.applyPrefill()
  },


  beforeDestroy () {
    this.cancelLookup()
  }
}

</script>

<style scoped>
/* The Client, the Site Name and the Site ID are fields of RtaRequestFields' left column (its `lead` slot), whose own rules give the form items and inputs the
   width of the column (up to 420px). The hint sits close under the Site Name (the error takes that place, so the hint drops below). */
.rta-note {display: block; line-height: 1.45; margin: 0 0 8px;}
.rta-create__name-hint {margin-top: -14px; font-size: 12px;}
.rta-create__name-hint.has-error {margin-top: 0;}

/* The hidden text of a live region (this repo has no global .sr-only). */
.rta-sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  margin: -1px;
  padding: 0;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}
</style>
